/**
 * AssociationsPage — module Associations côté mairie (Lot 1, L-ASSO-1.1).
 *
 * Annuaire des associations (liste + filtres + recherche) + création/édition d'une
 * fiche association via Dialog. Les onglets Représentants / Documents arriveront
 * aux livraisons 1.2 / 1.3. Accent violet « asso ».
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Search, Pencil, Power, Building2, Trash2, Users2, FileCheck, Settings, BadgeCheck } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Card, Button, Field, Input, Alert, Badge, SectionEyebrow } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { AppHeader } from '@/components/AppHeader';
import { ActeursLocauxManager } from '@/components/associations/ActeursLocauxManager';
import { RepresentantsManager } from './RepresentantsManager';
import { AssociationDossierManager } from './AssociationDossierManager';
import { useAuth } from '@/lib/auth-context';
import { useToast } from '@/components/ui/Toast';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import {
  ASSOCIATION_DOMAINES,
} from '@roronoa-collectivites/shared-types';
import type {
  AssociationResume, AssociationDetail, UpsertAssociationInput, AssociationsDashboard,
} from '@roronoa-collectivites/shared-types';
import type { AssociationVerifiee, SubventionsAssoRecuperees } from '@/lib/api';

const EMPTY_FORM: UpsertAssociationInput = {
  nom: '', objet: '', domaine: 'autre', rna: '', siret: '',
  email: '', telephone: '', adresse: '', codePostal: '', ville: '', siteWeb: '',
  ribIban: '', ribBic: '', ribTitulaire: '', visibleAnnuairePublic: false,
};

function domaineLabel(v: string): string {
  return ASSOCIATION_DOMAINES.find((d) => d.value === v)?.label ?? v;
}

/** Force le RNA en majuscules (W…), retire les espaces. */
function formatRna(v: string): string {
  return v.replace(/\s+/g, '').toUpperCase();
}
/** Formate l'IBAN à la saisie : majuscules + groupes de 4 séparés par un espace. */
function formatIban(v: string): string {
  const clean = v.replace(/\s+/g, '').toUpperCase();
  return clean.replace(/(.{4})/g, '$1 ').trim();
}
/** BIC en majuscules sans espaces. */
function formatBic(v: string): string {
  return v.replace(/\s+/g, '').toUpperCase();
}

export function AssociationsPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirm, dialogElement } = useConfirmDialog();
  // Suppression définitive réservée au maire / adjoint (admin) et super_admin.
  const canDelete = !!user && (user.role === 'admin' || user.role === 'super_admin');
  // Édition / (dés)activation : maire, adjoint, manager — PAS l'agent (lecture seule).
  const canManage = !!user && (user.role === 'super_admin' || user.role === 'admin' || user.role === 'manager');

  const [list, setList] = useState<AssociationResume[]>([]);
  const [dashboard, setDashboard] = useState<AssociationsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filtreDomaine, setFiltreDomaine] = useState('');
  const [recherche, setRecherche] = useState('');
  const [voirInactives, setVoirInactives] = useState(false);

  // Dialog fiche
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<UpsertAssociationInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Dialog représentants (mairie)
  const [repsOpen, setRepsOpen] = useState(false);
  const [repsCible, setRepsCible] = useState<{ id: string; nom: string } | null>(null);

  // Demandes de création en attente (mairie)
  const [demandes, setDemandes] = useState<AssociationResume[]>([]);

  // Dialog dossier de validation (staff)
  const [dossierOpen, setDossierOpen] = useState(false);
  const [dossierCible, setDossierCible] = useState<{ id: string; nom: string } | null>(null);

  // API Entreprise (données certifiées) : vérif identité + subventions perçues.
  const [entrepriseBusy, setEntrepriseBusy] = useState<'verif' | 'subv' | null>(null);
  const [entrepriseVerif, setEntrepriseVerif] = useState<AssociationVerifiee | null>(null);
  const [entrepriseSubv, setEntrepriseSubv] = useState<SubventionsAssoRecuperees | null>(null);
  const [entrepriseErreur, setEntrepriseErreur] = useState<string | null>(null);

  async function verifierEntreprise() {
    if (!editId) return;
    setEntrepriseBusy('verif'); setEntrepriseErreur(null);
    try {
      const siret = (form.siret ?? '').replace(/\s/g, '') || undefined;
      const v = await api.associations.verificationEntreprise(editId, siret);
      setEntrepriseVerif(v);
      if (v.source === 'mock') toast.info('Mode démonstration (API Entreprise non activée)');
    } catch (e) {
      const msg = e instanceof ApiException ? e.message : 'Vérification impossible';
      setEntrepriseErreur(msg); toast.error(msg);
    } finally { setEntrepriseBusy(null); }
  }

  async function chargerSubventionsEntreprise() {
    if (!editId) return;
    setEntrepriseBusy('subv'); setEntrepriseErreur(null);
    try {
      const siret = (form.siret ?? '').replace(/\s/g, '') || undefined;
      const rna = (form.rna ?? '').replace(/\s/g, '') || undefined;
      const s = await api.associations.subventionsEntreprise(editId, { siret, rna });
      setEntrepriseSubv(s);
      if (s.source === 'mock') toast.info('Mode démonstration (API Entreprise non activée)');
    } catch (e) {
      const msg = e instanceof ApiException ? e.message : 'Récupération des subventions impossible';
      setEntrepriseErreur(msg); toast.error(msg);
    } finally { setEntrepriseBusy(null); }
  }

  async function charger() {
    setLoading(true); setError(null);
    try {
      const [items, dash] = await Promise.all([
        api.associations.list({
          domaine: filtreDomaine || undefined,
          actif: voirInactives ? undefined : true,
          q: recherche.trim() || undefined,
        }),
        api.associations.dashboard().catch(() => null),
      ]);
      setList(items);
      setDashboard(dash);
      // Demandes de création en attente (staff gestion uniquement)
      if (canManage) {
        try { setDemandes(await api.associations.list({ statutValidation: 'en_attente' })); }
        catch { setDemandes([]); }
      }
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { charger(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filtreDomaine, voirInactives]);

  function ouvrirCreation() {
    setEditId(null); setForm(EMPTY_FORM); setFormError(null);
    setEntrepriseVerif(null); setEntrepriseSubv(null); setEntrepriseErreur(null);
    setOpen(true);
  }

  async function ouvrirEdition(id: string) {
    setFormError(null);
    setEntrepriseVerif(null); setEntrepriseSubv(null); setEntrepriseErreur(null);
    try {
      const d: AssociationDetail = await api.associations.get(id);
      setEditId(id);
      setForm({
        nom: d.nom, objet: d.objet ?? '', domaine: d.domaine, rna: d.rna ?? '', siret: d.siret ?? '',
        email: d.email ?? '', telephone: d.telephone ?? '', adresse: d.adresse ?? '',
        codePostal: d.codePostal ?? '', ville: d.ville ?? '', siteWeb: d.siteWeb ?? '',
        // ribIban laissé undefined : on ne réécrit le RIB que si l'utilisateur le saisit
        ribIban: undefined, ribBic: d.ribBic ?? '', ribTitulaire: d.ribTitulaire ?? '',
        visibleAnnuairePublic: d.visibleAnnuairePublic,
      });
      setOpen(true);
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Impossible d\'ouvrir la fiche');
    }
  }

  async function enregistrer() {
    if (!form.nom || form.nom.trim().length < 2) { setFormError('Le nom doit comporter au moins 2 caractères.'); return; }
    setSaving(true); setFormError(null);
    try {
      if (editId) {
        await api.associations.update(editId, form);
        toast.success('Association mise à jour');
      } else {
        await api.associations.create(form);
        toast.success('Association créée');
      }
      setOpen(false);
      await charger();
    } catch (e) {
      setFormError(e instanceof ApiException ? e.message : 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  }

  async function desactiver(a: AssociationResume) {
    const ok = await confirm({
      title: 'Désactiver l\'association',
      description: `Désactiver « ${a.nom} » ? Elle n'apparaîtra plus dans l'annuaire actif (réactivable ensuite).`,
      confirmLabel: 'Désactiver',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.associations.desactiver(a.id);
      toast.success('Association désactivée');
      await charger();
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Erreur');
    }
  }

  async function reactiver(a: AssociationResume) {
    const ok = await confirm({
      title: 'Réactiver l\'association',
      description: `Réactiver « ${a.nom} » ? Elle réapparaîtra dans l'annuaire actif.`,
      confirmLabel: 'Réactiver',
    });
    if (!ok) return;
    try {
      await api.associations.reactiver(a.id);
      toast.success('Association réactivée');
      await charger();
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Erreur');
    }
  }

  async function supprimer(a: AssociationResume) {
    const ok = await confirm({
      title: 'Supprimer définitivement',
      description: `Supprimer DÉFINITIVEMENT « ${a.nom} » ? Cette action est irréversible et retire l'association, ses représentants et ses documents. Pour un simple retrait réversible, utilisez plutôt « Désactiver ».`,
      confirmLabel: 'Supprimer définitivement',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.associations.supprimerDefinitif(a.id);
      toast.success('Association supprimée');
      await charger();
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Erreur');
    }
  }

  const set = (patch: Partial<UpsertAssociationInput>) => setForm((f) => ({ ...f, ...patch }));

  async function statuerCreation(a: AssociationResume, decision: 'valide' | 'refuse') {
    const ok = await confirm({
      title: decision === 'valide' ? 'Valider la création' : 'Refuser la création',
      description: decision === 'valide'
        ? `Valider la création de « ${a.nom} » ? Elle deviendra active et visible dans l'annuaire.`
        : `Refuser la demande de création de « ${a.nom} » ?`,
      confirmLabel: decision === 'valide' ? 'Valider' : 'Refuser',
      danger: decision === 'refuse',
    });
    if (!ok) return;
    try {
      await api.associations.statuerCreation(a.id, decision);
      toast.success(decision === 'valide' ? 'Association validée' : 'Demande refusée');
      await charger();
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Erreur');
    }
  }

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Association / Acteurs locaux" accent="asso" />

      <main id="main-content" className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        {/* Titre de page */}
        <div className="mb-6 flex items-start justify-between gap-3">
          <div>
            <SectionEyebrow>Associations</SectionEyebrow>
            <h1 className="mt-1 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
              Annuaire des associations
            </h1>
            {/* 9.3 — l'entrée vers les accompagnements. Un écran sans porte
                d'entrée est un écran que personne ne trouve. */}
            <a href="/associations/accompagnements"
              className="mt-1 inline-block text-sm text-asso-fg hover:underline">
              Accompagnements proposés par la mairie
            </a>
          </div>
          {canManage && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/grc/dossiers-associations')}>
              <Settings className="h-4 w-4" /> Dossiers & pièces (GRC)
            </Button>
          )}
        </div>

        {/* KPI amorce */}
        {dashboard && (
          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Card variant="glass" padded>
              <div className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Associations recensées</div>
              <div className="mt-1 font-display text-2xl font-semibold text-ink">{dashboard.nbAssociations}</div>
              {dashboard.nbNouvellesCeMois > 0 && (
                <div className="mt-0.5 text-xs text-asso-fg">+{dashboard.nbNouvellesCeMois} ce mois</div>
              )}
            </Card>
            <Card variant="glass" padded>
              <div className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Représentants en attente</div>
              <div className="mt-1 font-display text-2xl font-semibold text-ink">{dashboard.nbRepresentantsEnAttente}</div>
              <div className="mt-0.5 text-xs text-ink-subtle">à valider (livraison suivante)</div>
            </Card>
            <Card variant="glass" padded className="flex items-center justify-center">
              {canManage ? (
                <Button variant="asso" onClick={ouvrirCreation}>
                  <Plus className="h-4 w-4" /> Nouvelle association
                </Button>
              ) : (
                <span className="text-xs text-ink-subtle text-center">Consultation de l'annuaire</span>
              )}
            </Card>
          </div>
        )}

        {/* Demandes de création en attente (mairie) */}
        {canManage && demandes.length > 0 && (
          <Card variant="glass" padded className="mb-6 border-warning/40">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
              <span>Demandes de création à valider</span>
              <Badge variant="warning">{demandes.length}</Badge>
            </div>
            <div className="space-y-2">
              {demandes.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-2 rounded-r-md border border-border bg-surface/40 p-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-sm text-ink">{d.nom}</div>
                    <div className="truncate text-xs text-ink-subtle">{domaineLabel(d.domaine)}{d.ville ? ` · ${d.ville}` : ''}</div>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button variant="ghost" size="sm" onClick={() => statuerCreation(d, 'valide')} className="text-success hover:bg-success/10 hover:text-success">Valider</Button>
                    <Button variant="ghost" size="sm" onClick={() => statuerCreation(d, 'refuse')} className="text-urgent hover:bg-urgent/10 hover:text-urgent">Refuser</Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Filtres */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" aria-hidden="true" />
            <Input
              className="pl-9"
              placeholder="Rechercher une association…"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') charger(); }}
            />
          </div>
          <select
            className="h-9 rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink backdrop-blur-glass focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-asso"
            value={filtreDomaine}
            onChange={(e) => setFiltreDomaine(e.target.value)}
          >
            <option value="">Tous les domaines</option>
            {ASSOCIATION_DOMAINES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-ink-muted">
            <input type="checkbox" className="rounded border-border text-asso focus-visible:ring-2 focus-visible:ring-asso"
              checked={voirInactives} onChange={(e) => setVoirInactives(e.target.checked)} />
            Inclure inactives
          </label>
          <Button variant="ghost" onClick={charger}>Filtrer</Button>
        </div>

        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        {/* Liste */}
        {loading ? (
          <Card variant="glass" padded><div className="py-8 text-center text-sm text-ink-subtle">Chargement…</div></Card>
        ) : list.length === 0 ? (
          <Card variant="glass" padded>
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <Building2 className="h-8 w-8 text-ink-subtle" aria-hidden="true" />
              <div className="text-sm text-ink-muted">Aucune association pour le moment.</div>
              {canManage && (
                <Button variant="asso" onClick={ouvrirCreation}><Plus className="h-4 w-4" /> Créer la première</Button>
              )}
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((a) => (
              <Card key={a.id} variant="glass" padded className={a.actif ? '' : 'opacity-60'}>
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-r-lg bg-asso-tint/70 text-asso-fg">
                    {a.logoUrl ? <img src={a.logoUrl} alt="" className="h-full w-full object-cover" /> : <Users className="h-5 w-5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="truncate font-medium text-ink">{a.nom}</div>
                      {!a.actif && <Badge variant="neutral" size="sm">Inactive</Badge>}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                      <Badge variant="asso" size="sm">{domaineLabel(a.domaine)}</Badge>
                      {a.visibleAnnuairePublic && <Badge variant="success" size="sm">Annuaire public</Badge>}
                    </div>
                    <div className="mt-1.5 space-y-0.5 text-xs text-ink-subtle">
                      {a.ville && <div>{a.ville}</div>}
                      <div>{a.nbRepresentants} représentant{a.nbRepresentants !== 1 ? 's' : ''}</div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {canManage && (
                        <Button variant="ghost" size="sm" onClick={() => ouvrirEdition(a.id)}>
                          <Pencil className="h-3.5 w-3.5" /> Éditer
                        </Button>
                      )}
                      {canManage && (
                        <Button variant="ghost" size="sm" onClick={() => { setRepsCible({ id: a.id, nom: a.nom }); setRepsOpen(true); }}>
                          <Users2 className="h-3.5 w-3.5" /> Représentants
                        </Button>
                      )}
                      {canManage && (
                        <Button variant="ghost" size="sm" onClick={() => { setDossierCible({ id: a.id, nom: a.nom }); setDossierOpen(true); }}>
                          <FileCheck className="h-3.5 w-3.5" /> Dossier
                        </Button>
                      )}
                      {canManage && a.actif && (
                        <Button variant="ghost" size="sm" onClick={() => desactiver(a)}>
                          <Power className="h-3.5 w-3.5" /> Désactiver
                        </Button>
                      )}
                      {canManage && !a.actif && (
                        <Button variant="ghost" size="sm" onClick={() => reactiver(a)}
                          className="text-success hover:bg-success/10 hover:text-success">
                          <Power className="h-3.5 w-3.5" /> Réactiver
                        </Button>
                      )}
                      {canDelete && (
                        <Button variant="ghost" size="sm" onClick={() => supprimer(a)}
                          className="text-urgent hover:bg-urgent/10 hover:text-urgent">
                          <Trash2 className="h-3.5 w-3.5" /> Supprimer
                        </Button>
                      )}
                      {!canManage && !canDelete && (
                        <span className="text-xs text-ink-subtle">Lecture seule</span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* ===== Acteurs locaux & producteurs (migration 131) ===== */}
        <div className="mt-10">
          <SectionEyebrow>Acteurs locaux</SectionEyebrow>
          <h2 className="mt-1 font-display text-lg font-semibold tracking-r-tight text-ink sm:text-xl">
            Commerces, artisans & producteurs
          </h2>
          <p className="mt-1.5 text-sm text-ink-muted">
            Annuaire économique de la commune. Les producteurs peuvent proposer leurs paniers et produits
            en circuit court, visibles par les habitants.
          </p>
          <div className="mt-4">
            <ActeursLocauxManager />
          </div>
        </div>
      </main>

      {/* Dialog fiche identité */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={editId ? 'Modifier l\'association' : 'Nouvelle association'}
        size="lg"
      >
        <div className="space-y-4">
          {formError && <Alert variant="error">{formError}</Alert>}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Nom de l'association" htmlFor="asso-nom" required>
              <Input id="asso-nom" value={form.nom} onChange={(e) => set({ nom: e.target.value })} placeholder="Tennis Club de…" />
            </Field>
            <Field label="Domaine" htmlFor="asso-domaine">
              <select
                id="asso-domaine"
                className="h-9 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-asso"
                value={form.domaine} onChange={(e) => set({ domaine: e.target.value as UpsertAssociationInput['domaine'] })}
              >
                {ASSOCIATION_DOMAINES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Objet (loi 1901)" htmlFor="asso-objet">
            <textarea
              id="asso-objet"
              className="min-h-[64px] w-full rounded-r-md border border-border bg-surface/60 px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-asso"
              value={form.objet ?? ''} onChange={(e) => set({ objet: e.target.value })}
              placeholder="Promotion et pratique du tennis…"
            />
          </Field>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="N° RNA" htmlFor="asso-rna" hint="Format W + 9 chiffres">
              <Input id="asso-rna" value={form.rna ?? ''} onChange={(e) => set({ rna: formatRna(e.target.value) })} placeholder="W123456789" />
            </Field>
            <Field label="SIRET" htmlFor="asso-siret" hint="14 chiffres (optionnel)">
              <Input id="asso-siret" value={form.siret ?? ''} onChange={(e) => set({ siret: e.target.value })} placeholder="12345678900012" />
            </Field>
          </div>

          {editId && (
            <div className="rounded-r-lg border border-asso/25 bg-asso-tint/20 p-3.5">
              <div className="flex flex-wrap items-center gap-2">
                <BadgeCheck className="h-4 w-4 text-asso" aria-hidden="true" />
                <span className="text-sm font-medium text-ink">Données certifiées (API Entreprise)</span>
                <span className="text-xs text-ink-subtle">— identité & subventions officielles de l'État</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="secondary" size="sm" onClick={verifierEntreprise}
                  disabled={entrepriseBusy !== null || !(form.siret && form.siret.replace(/\s/g, '').length >= 9)}>
                  {entrepriseBusy === 'verif' ? 'Vérification…' : 'Vérifier l\'identité'}
                </Button>
                <Button variant="secondary" size="sm" onClick={chargerSubventionsEntreprise}
                  disabled={entrepriseBusy !== null || !((form.siret && form.siret.replace(/\s/g, '').length >= 9) || (form.rna && form.rna.length >= 10))}>
                  {entrepriseBusy === 'subv' ? 'Chargement…' : 'Voir les subventions'}
                </Button>
              </div>
              {!(form.siret && form.siret.replace(/\s/g, '').length >= 9) && (
                <p className="mt-2 text-xs text-ink-subtle">Renseignez le SIRET (ou le RNA pour les subventions) pour interroger l'API.</p>
              )}

              {entrepriseErreur && (
                <div className="mt-3"><Alert variant="warning">{entrepriseErreur}</Alert></div>
              )}

              {entrepriseVerif && (
                <div className="mt-3 rounded-r-md border border-border bg-surface/60 p-3 text-sm">
                  <div className="font-medium text-ink">{entrepriseVerif.raisonSociale ?? 'Raison sociale non renseignée'}</div>
                  <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-ink-muted">
                    {entrepriseVerif.siren && <div>SIREN : <span className="text-ink">{entrepriseVerif.siren}</span></div>}
                    {entrepriseVerif.rna && <div>RNA : <span className="text-ink">{entrepriseVerif.rna}</span></div>}
                    {entrepriseVerif.formeJuridique && <div>Forme juridique : <span className="text-ink">{entrepriseVerif.formeJuridique}</span></div>}
                    {entrepriseVerif.siretSiege && <div>SIRET siège : <span className="text-ink">{entrepriseVerif.siretSiege}</span></div>}
                  </div>
                </div>
              )}

              {entrepriseSubv && (
                <div className="mt-3 rounded-r-md border border-border bg-surface/60 p-3 text-sm">
                  <div className="font-medium text-ink mb-1.5">
                    Subventions {entrepriseSubv.subventions.length > 0 ? `(${entrepriseSubv.subventions.length})` : ''}
                  </div>
                  {entrepriseSubv.subventions.length === 0 ? (
                    <p className="text-xs text-ink-subtle">Aucune subvention connue pour cette association.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {entrepriseSubv.subventions.map((s, i) => (
                        <li key={i} className="flex items-baseline justify-between gap-3 text-xs">
                          <span className="text-ink">
                            {s.dispositif ?? 'Dispositif inconnu'}
                            {s.annee ? <span className="text-ink-subtle"> · {s.annee}</span> : null}
                            {s.attribuant ? <span className="text-ink-subtle"> · {s.attribuant}</span> : null}
                          </span>
                          <span className="whitespace-nowrap tabular-nums text-ink">
                            {typeof s.montantAccorde === 'number'
                              ? `${s.montantAccorde.toLocaleString('fr-FR')} €`
                              : typeof s.montantDemande === 'number'
                                ? `${s.montantDemande.toLocaleString('fr-FR')} € (demandé)`
                                : '—'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Email" htmlFor="asso-email"><Input id="asso-email" type="email" value={form.email ?? ''} onChange={(e) => set({ email: e.target.value })} /></Field>
            <Field label="Téléphone" htmlFor="asso-tel"><Input id="asso-tel" value={form.telephone ?? ''} onChange={(e) => set({ telephone: e.target.value })} /></Field>
          </div>

          <Field label="Adresse" htmlFor="asso-adr"><Input id="asso-adr" value={form.adresse ?? ''} onChange={(e) => set({ adresse: e.target.value })} /></Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Code postal" htmlFor="asso-cp"><Input id="asso-cp" value={form.codePostal ?? ''} onChange={(e) => set({ codePostal: e.target.value })} /></Field>
            <Field label="Ville" htmlFor="asso-ville"><Input id="asso-ville" value={form.ville ?? ''} onChange={(e) => set({ ville: e.target.value })} /></Field>
            <Field label="Site web" htmlFor="asso-web"><Input id="asso-web" value={form.siteWeb ?? ''} onChange={(e) => set({ siteWeb: e.target.value })} /></Field>
          </div>

          {/* RIB (sert aux subventions). IBAN chiffré côté serveur, jamais réaffiché en clair. */}
          <div className="rounded-r-lg border border-border bg-surface/40 p-3">
            <div className="mb-2 text-sm font-medium text-ink">Coordonnées bancaires (RIB)</div>
            <p className="mb-3 text-xs text-ink-subtle">
              {editId
                ? 'Laissez l\'IBAN vide pour ne pas le modifier. L\'IBAN enregistré est chiffré et n\'est jamais réaffiché en clair.'
                : 'Optionnel ici, requis pour les demandes de subvention. L\'IBAN est chiffré au stockage.'}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="IBAN" htmlFor="asso-iban">
                <Input id="asso-iban" value={form.ribIban ?? ''} onChange={(e) => set({ ribIban: formatIban(e.target.value) })} placeholder={editId ? 'Inchangé' : 'FR76 …'} />
              </Field>
              <Field label="BIC" htmlFor="asso-bic"><Input id="asso-bic" value={form.ribBic ?? ''} onChange={(e) => set({ ribBic: formatBic(e.target.value) })} /></Field>
            </div>
            <Field label="Titulaire du compte" htmlFor="asso-rib-titulaire"><Input id="asso-rib-titulaire" value={form.ribTitulaire ?? ''} onChange={(e) => set({ ribTitulaire: e.target.value })} /></Field>
          </div>

          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" className="rounded border-border text-asso focus-visible:ring-2 focus-visible:ring-asso"
              checked={!!form.visibleAnnuairePublic} onChange={(e) => set({ visibleAnnuairePublic: e.target.checked })} />
            Afficher cette association dans l'annuaire public (visible des citoyens)
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Annuler</Button>
            <Button variant="asso" onClick={enregistrer} disabled={saving}>
              {saving ? 'Enregistrement…' : editId ? 'Enregistrer' : 'Créer'}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Dialog représentants (mairie) */}
      <Dialog
        open={repsOpen}
        onClose={() => { setRepsOpen(false); charger(); }}
        title={repsCible ? `Représentants — ${repsCible.nom}` : 'Représentants'}
        size="lg"
      >
        {repsCible && <RepresentantsManager associationId={repsCible.id} associationNom={repsCible.nom} />}
      </Dialog>

      {/* Dialog dossier de validation (staff) */}
      <Dialog
        open={dossierOpen}
        onClose={() => { setDossierOpen(false); charger(); }}
        title={dossierCible ? `Dossier — ${dossierCible.nom}` : 'Dossier'}
        size="lg"
      >
        {dossierCible && <AssociationDossierManager associationId={dossierCible.id} />}
      </Dialog>

      {/* Boîte de confirmation (Désactiver / Supprimer) — sans cet élément monté,
          confirm() ne se résout jamais et les boutons restent sans effet. */}
      {dialogElement}
    </div>
  );
}
